import { Container } from "@/shared/components/Container";
import { Reveal } from "@/shared/components/Reveal";
import {
  usePublicServerData,
  type PublicTestimonialCard,
} from "@/features/content/context/PublicServerDataContext";
import { useLocale } from "@/shared/i18n/locale";
import { getHomeContent, type FallbackTestimonialContent } from "@/content/home.content";
import { useTestimonialsCarousel } from "./useTestimonialsCarousel";
import styles from "./Testimonials.module.css";

function QuoteIcon() {
  return <svg className={styles.quoteIcon} viewBox="0 0 26 26" fill="none" aria-hidden="true"><path d="M5.42 4.33h15.16c.6 0 1.09.49 1.09 1.09v11.91c0 .6-.49 1.09-1.09 1.09H9.75L5.42 22.75v-4.33c-.6 0-1.09-.49-1.09-1.09V5.42c0-.6.49-1.09 1.09-1.09Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>;
}

function ArrowIcon({ previous = false }: { previous?: boolean }) {
  return <svg className={previous ? styles.arrowPrevious : undefined} width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M3.75 9h10.5M10.5 5.25 14.25 9l-3.75 3.75" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function chunk<T>(items: T[], size: number): T[][] {
  const pages: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    pages.push(items.slice(index, index + size));
  }
  return pages;
}

export function Testimonials() {
  const locale = useLocale();
  const content = getHomeContent(locale);

  // Tabela vazia ou SSR sem dados: cai no array estático e a home fica igual.
  // Em EN a lista do servidor vale mesmo vazia: só sai depoimento com tradução
  // revisada no CMS, e o array estático em inglês contornaria essa revisão.
  const serverTestimonials = usePublicServerData()?.testimonials;
  const useServerList =
    serverTestimonials !== undefined && (serverTestimonials.length > 0 || locale === "en");
  const testimonials: (FallbackTestimonialContent | PublicTestimonialCard)[] = useServerList
    ? serverTestimonials
    : content.testimonials.fallbacks;
  const carousel = useTestimonialsCarousel(testimonials.length);
  const pages = chunk(testimonials, carousel.cardsPerPage);

  if (!testimonials.length) return null;

  return (
    <section id="depoimentos" className={styles.section}>
      <Container>
        <Reveal as="header" className={styles.header}>
          <span className={styles.eyebrow}>{content.testimonials.eyebrow}</span>
          <h2 className={styles.title}>{content.testimonials.title}</h2>
          <p className={styles.subtitle}>{content.testimonials.subtitle}</p>
        </Reveal>
        <Reveal delay={80}>
          <div className={styles.viewport} aria-label={content.testimonials.ariaLabel} onPointerDown={carousel.handlePointerDown} onPointerUp={carousel.handlePointerUp}>
            <div
              className={styles.track}
              data-carousel-track
              style={{ transform: `translateX(${carousel.page * -100}%)` }}
            >
              {pages.map((pageTestimonials, pageIndex) => (
                <div
                  className={styles.page}
                  data-carousel-page
                  key={pageTestimonials.map((testimonial) => testimonial.author).join("-")}
                  aria-hidden={pageIndex === carousel.page ? undefined : "true"}
                >
                  {pageTestimonials.map((testimonial) => (
                    <article
                      className={styles.card}
                      key={
                        "id" in testimonial
                          ? testimonial.id
                          : `${testimonial.company}-${testimonial.author}`
                      }
                    >
                      <QuoteIcon />
                      <div className={styles.identity}>
                        <h3 className={styles.author}>{testimonial.author}</h3>
                        <span className={styles.company}>{testimonial.company}</span>
                      </div>
                      <p className={styles.quote}>{testimonial.quote}</p>
                    </article>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal className={styles.controls} delay={140}>
          <button type="button" className={styles.arrow} aria-label={content.testimonials.prevAria} onClick={carousel.showPrevious}><ArrowIcon previous /></button>
          <button type="button" className={styles.arrow} aria-label={content.testimonials.nextAria} onClick={carousel.showNext}><ArrowIcon /></button>
        </Reveal>
      </Container>
    </section>
  );
}
